import gsap from "gsap";
import * as PIXI from "pixi.js";
import { AppCore } from "../../api/core/AppCore";
import { AssetLoader } from "../../loader/asset/AssetLoader";
import { LoaderEvent } from "../../loader/event/LoaderEvent";
import { DeviceUtil } from "../../util/device/DeviceUtil";
import { EventObject } from "../../util/event/EventObject";
import { Logger } from "../../util/log/Logger";
import { NumberUtil } from "../../util/number/NumberUtil";
import { Delay } from "../../util/time/Delay";
import { GraphicsUtil } from "../../util/view/GraphicsUtil";
import { CommonGameEvent } from "../controller/event/CommonGameEvent";
import { FactoryManager } from "../factory/FactoryManager";
import { OrientationType } from "./constant/RendererConstants";
import { GameFocusEvent } from "./FocusEvent";
/**
 * Renderer.ts
 */
export class Renderer {
    constructor() {
        this.ANALYTICS_RESIZE_DELAY = 30;
        this.mousePosition = new PIXI.Point();
        if (Renderer.instance) {
            throw new Error("Renderer Singleton Already Initialised");
        }
        Renderer.instance = this;
    }
    initialise(properties) {
        this.properties = properties;
        this.verticalOffSet = 0;
        this.minimumScale = 0.5;
        this.setResolution();
        this.setFrameRate();
        this.create();
        this.addEventListeners();
        this.registerDocument();
        this.start();
        return this;
    }
    create() {
        this.createBody();
        this.createCore();
        this.createView();
        this.createRenderer();
        this.createStage();
        this.createHolder();
        this.createMeter();
        this.createScreenReaderRegion();
        this.shimAccessibilityUpdate();
        this.handleResize();
        //Analytics - report if starting in an iFrame
        const inIframe = window.self !== window.top;
        this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), { category: "TITAN_STATE", operation: "WINDOW_IN_IFRAME", value: inIframe.toString() }));
        this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), { category: "TITAN_STATE", operation: "DEVICE_ORIENTATION", value: this.orientation }));
        this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), { category: "TITAN_STATE", operation: "USER_AGENT", value: window.navigator.userAgent }));
        Logger.addObjectRef(this, "renderer");
    }
    addEventListeners() {
        this.listenCore(LoaderEvent.PRELOADER_READY, this.handlePreloaderReady, this);
        this.listenCore(LoaderEvent.LOADER_READY, this.handleLoaderReady, this);
        document.addEventListener("visibilitychange", this.handleFocusChanged.bind(this));
        window.addEventListener("resize", this.handleWindowResize.bind(this));
    }
    setResolution() {
        let resolution = window.devicePixelRatio;
        if (isNaN(resolution) || resolution < 1) {
            resolution = 1;
        }
        //Force max resolution of 2 to avoid running out of memory
        this.properties.resolution = resolution;
        Renderer.textFieldResolution = resolution;
    }
    setFrameRate() {
        this.ticker = gsap.ticker;
        this.ticker.deltaRatio(this.properties.targetFrameRate);
    }
    createCore() {
        this.core = AppCore.getInstance();
    }
    registerDocument() {
        if (this.properties.preventTouchMove) {
            document.addEventListener("touchmove", function (event) {
                event.preventDefault();
            });
        }
    }
    clearMask() {
        if (this.mask) {
            this.holder.removeChild(this.mask);
            this.mask.destroy();
            this.holder.mask = null;
        }
    }
    createMask(viewport) {
        let size = new PIXI.Point(viewport.width, viewport.height);
        this.mask = GraphicsUtil.createMask(size);
        this.holder.addChild(this.mask);
        this.holder.mask = this.mask;
    }
    hideMask() {
        if (this.mask) {
            this.holder.mask = null;
            this.mask.visible = false;
        }
    }
    showMask() {
        if (this.mask) {
            this.holder.mask = this.mask;
            this.mask.visible = true;
        }
    }
    updateProperties(viewport) {
        this.properties.viewport = viewport;
        this.handleResize();
    }
    createView() {
        let windowSize = this.getWindowSize();
        if (this.properties.view) {
            this.view = this.properties.view;
        }
        else {
            this.createCanvas(windowSize);
        }
    }
    createCanvas(windowSize) {
        this.view = document.createElement("canvas");
        this.view.width = windowSize.x;
        this.view.height = windowSize.y;
        this.view.style.position = "fixed";
        this.view.id = "game-canvas";
        this.properties.view = this.view;
        let holder = document.getElementById("game-holder");
        if (holder && (DeviceUtil.isIos() || DeviceUtil.isBrowserSamsung())) {
            holder.appendChild(this.view);
        }
        else {
            let gameGrid = document.getElementById("game-grid");
            if (gameGrid) {
                gameGrid.remove();
            }
            document.body.appendChild(this.view);
        }
    }
    handleFocusChanged() {
        this.dispatchCore(document.hidden ? GameFocusEvent.OUT : GameFocusEvent.IN);
    }
    createStage() {
        this.stage = new PIXI.Container();
        this.stage.name = "Stage";
        this.stage.eventMode = "passive";
    }
    createHolder() {
        this.holder = new PIXI.Container();
        this.holder.name = "holder";
        this.stage.addChild(this.holder);
    }
    createMeter() {
        if (this.properties.showFrameRate) {
            this.meter = new FPSMeter();
        }
    }
    createScreenReaderRegion() {
        this.screenReaderRegion = document.createElement("div");
        this.screenReaderRegion.setAttribute("aria-live", "polite");
        this.screenReaderRegion.style.position = "absolute";
        this.screenReaderRegion.style.left = "-10000px";
        this.screenReaderRegion.style.top = "-10000px";
        this.screenReaderRegion.style.width = "1px";
        this.screenReaderRegion.style.height = "1px";
        this.screenReaderRegion.style.overflow = "hidden";
        document.body.appendChild(this.screenReaderRegion);
        this.enableMouseTracking();
    }
    /**
     * Tracks the mouse position over the canvas and provides a method to check if the pointer is over a given displayObject.
     */
    enableMouseTracking() {
        if (!this.view)
            return;
        this.view.addEventListener('mousemove', (e) => {
            const rect = this.view.getBoundingClientRect();
            // Convert mouse position to canvas coordinates
            const x = (e.clientX - rect.left) * (this.view.width / rect.width);
            const y = (e.clientY - rect.top) * (this.view.height / rect.height);
            this.mousePosition.set(x, y);
        });
    }
    createBody() {
        if (DeviceUtil.isTouch()) {
            document.body.style.background = "#000000";
        }
    }
    createBackground(landscapeFileLocation, portraitFileLocation) {
        const factory = FactoryManager.getInstance().getFactory();
        const wrapperConfig = factory.getWrapperConfig();
        const game = wrapperConfig.getGame();
        if (landscapeFileLocation) {
            this.landscapeBackgroundLocation = /*AppData.DEPLOY_TYPE.isNativeLite() ? AppData.LITE_SERVER + game.getPath() + "/" +
                landscapeFileLocation :*/
                "./" + landscapeFileLocation;
        }
        if (portraitFileLocation) {
            this.portraitBackgroundLocation = /*AppData.DEPLOY_TYPE.isNativeLite() ? AppData.LITE_SERVER + game.getPath() + "/" +
                portraitFileLocation : */
                "./" + portraitFileLocation;
        }
    }
    getCurrentBackgroundLocation() {
        if (this.orientation === OrientationType.PORTRAIT && this.portraitBackgroundLocation) {
            return this.portraitBackgroundLocation;
        }
        return this.landscapeBackgroundLocation;
    }
    resizeBG() {
        if (this.backgroundImg) {
            let w = this.getView().width / window.devicePixelRatio;
            let h = this.getView().height / window.devicePixelRatio;
            this.backgroundImg.width = w;
            this.backgroundImg.height = h;
            this.backgroundImg.x = w / 2;
            this.backgroundImg.y = h / 2;
        }
    }
    setBackground(fileLocation) {
        if (fileLocation && fileLocation.length) {
            if (this.backgroundImg) {
                this.backgroundImg.removeFromParent();
                this.backgroundImg = null;
            }
            AssetLoader.loadImageFromUrl(fileLocation, (background) => {
                if (background) {
                    if (this.backgroundImg) {
                        this.backgroundImg.removeFromParent();
                        this.backgroundImg = null;
                    }
                    this.backgroundImg = background;
                    this.backgroundImg.anchor.set(0.5, 0.5);
                    this.stage.addChildAt(background, 0);
                    this.resizeBG();
                }
            }, this);
        }
        else {
            this.view.style.backgroundColor = "black";
        }
    }
    createRenderer() {
        // Firefox 72.0.0/72.0.1 introduced an issue where webGL Programs are no longer allowed > 16 samplers
        // WebGL warning: linkProgram: Programs with more than 16 samplers are disallowed on Mesa drivers to avoid crashing
        PIXI.BatchRenderer.defaultMaxTextures = Math.min(PIXI.BatchRenderer.defaultMaxTextures, 16);
        // Google Pixel 6 has a flickering issue with >64 sprites at least 1 sprite with a mask. this fixes it.
        PIXI.BatchRenderer.canUploadSameBuffer = false;
        this.renderer = this.createAutoRenderer();
        globalThis.__PIXI_STAGE__ = this.stage;
        globalThis.__PIXI_RENDERER__ = this.renderer;
    }
    createAutoRenderer() {
        return PIXI.autoDetectRenderer(this.properties.getJsonProperties());
    }
    createWebGLRenderer() {
        return new PIXI.Renderer(this.properties.getJsonProperties());
    }
    /*protected createCanvasRenderer(): PIXI.IRenderer {
        return new PIXI.CanvasRenderer(this.properties.getJsonProperties());
    }*/
    start() {
        let ticker = gsap.ticker;
        let renderFunction = this.properties.showFrameRate ? this.renderWithFrameRate : this.render;
        ticker.add(renderFunction.bind(this));
        gsap.globalTimeline.timeScale(1);
    }
    render() {
        this.renderer.render(this.stage, {});
    }
    renderWithFrameRate() {
        this.meter.tickStart();
        this.renderer.render(this.stage);
        this.meter.tick();
    }
    handlePreloaderReady() {
        this.handleResize();
    }
    handleLoaderReady() {
        this.setBackground(this.getCurrentBackgroundLocation());
    }
    handleResize() {
        if (this.resizeInProgress) {
            this.resizeWaiting = true;
        }
        else {
            this.resizeInProgress = true;
            this.dispatchResize();
            this.resizeInProgress = false;
            if (!this.analyticsResizeEventTimer) {
                this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), { category: "TITAN_STATE", operation: "WINDOW_SIZE_START", value: JSON.stringify({ width: window.innerWidth, height: window.innerHeight }) }));
                this.analyticsResizeEventTimer = gsap.delayedCall(this.ANALYTICS_RESIZE_DELAY, () => {
                    this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), { category: "TITAN_STATE", operation: "WINDOW_SIZE_END", value: JSON.stringify({ width: window.innerWidth, height: window.innerHeight }) }));
                    this.analyticsResizeEventTimer = null;
                });
            }
            if (this.resizeWaiting) {
                this.dispatchResize();
            }
        }
    }
    handleWindowResize() {
        if (DeviceUtil.isIos()) {
            this.handleResize();
            window.requestAnimationFrame(() => {
                Delay.create(500, this.handleResize, this);
            });
        }
        else {
            this.handleResize();
        }
    }
    forceResize(delay = 0) {
        if (delay) {
            Delay.create(delay, this.handleResize, this);
        }
        else {
            this.handleResize();
        }
    }
    forceTracking(delay = 0) {
        //no-op - this function is deprecated with the creation of the LocationManager
    }
    updateHolder(windowSize, scale) {
        let viewport = this.properties.viewport;
        let canvasWidth = viewport.width * scale.x;
        let canvasHeight = viewport.height * scale.y;
        let safeCanvasWidth = viewport.safeWidth * scale.x;
        let safeCanvasHeight = viewport.safeHeight * scale.y;
        let xOffset = Math.floor(((windowSize.x - safeCanvasWidth) / scale.x) / 2);
        let yOffset = Math.floor(((windowSize.y - safeCanvasHeight) / scale.y) / 2);
        let maxXOffset = Math.floor(((canvasWidth - safeCanvasWidth) / scale.x) / 2);
        let maxYOffset = Math.floor(((canvasHeight - safeCanvasHeight) / scale.y) / 2);
        xOffset = xOffset >= 0 ? Math.min(xOffset, maxXOffset) : 0;
        yOffset = yOffset < 0 ? 0 : yOffset > maxYOffset ? maxYOffset : yOffset;
        this.safeTransform = new PIXI.Point(xOffset, yOffset);
        this.holder.scale.x = scale.x;
        this.holder.scale.y = scale.y;
        this.holder.x = Math.round((windowSize.x - canvasWidth) / 2);
        this.holder.y = Math.round((windowSize.y - canvasHeight) / 2);
    }
    updateOrientation(size) {
        let viewport = this.properties.viewport;
        if (viewport.isLandscape()) {
            if (size.x > size.y) {
                this.orientation = OrientationType.LANDSCAPE;
                this.rotated = false;
            }
            else {
                this.orientation = OrientationType.PORTRAIT;
                this.rotated = true;
            }
        }
        else {
            if (size.x > size.y) {
                this.orientation = OrientationType.LANDSCAPE;
                this.rotated = true;
            }
            else {
                this.orientation = OrientationType.PORTRAIT;
                this.rotated = false;
            }
        }
        if (this.rotated) {
            this.dispatchCore(ResizeEvent.PRE_ROTATE);
            this.dispatchCore(ResizeEvent.DEINITIALISE_TEXT);
            this.dispatchCore(new EventObject(CommonGameEvent.BI_KPI.getType(), { category: "TITAN_STATE", operation: "ORIENTATION", value: this.orientation }));
            this.setBackground(this.getCurrentBackgroundLocation());
        }
    }
    getScale(windowSize) {
        let viewport = this.properties.viewport;
        let scale;
        if (viewport.isLandscape()) {
            if (windowSize.x > windowSize.y) {
                scale = windowSize.y / viewport.safeHeight;
            }
            else {
                scale = windowSize.x / viewport.safeHeight;
            }
        }
        else {
            if (windowSize.x > windowSize.y) {
                scale = windowSize.y / viewport.safeWidth;
            }
            else {
                let yScale = windowSize.y / viewport.safeHeight;
                let xScale = windowSize.x / viewport.safeWidth;
                scale = Math.min(xScale, yScale);
            }
        }
        scale = NumberUtil.round(scale);
        if (scale >= 1 && this.properties.restrictScale) {
            scale = 1;
        }
        return new PIXI.Point(scale, scale);
    }
    dispatchResize() {
        if (this.properties.autoResize) {
            this.holder.visible = false;
            this.resizeWaiting = false;
            let oldMask = this.holder.mask;
            this.clearMask();
            let windowSize = this.getWindowSize();
            let scale = this.getScale(windowSize);
            this.renderer.resize(windowSize.x, windowSize.y);
            this.updateOrientation(windowSize);
            this.updateHolder(windowSize, scale);
            this.dispatchCore(ResizeEvent.RESIZE);
            if (DeviceUtil.isIos() && DeviceUtil.isBrowserSafari()) {
                window.scrollTo(0, 0);
            }
            if (oldMask) {
                this.createMask(this.getViewport());
            }
            this.holder.visible = true;
            this.resizeBG();
            this.dispatchCore(ResizeEvent.TRACK_TARGETS);
        }
    }
    listenCore(type, handler, scope) {
        this.core.addEventListener(type.getType(), handler, scope);
    }
    dispatchCore(event) {
        this.core.dispatchEvent(event);
    }
    shimAccessibilityUpdate() {
        //adds support for circular hitareas on the accessibility plugin
        const manager = this.renderer.plugins.accessibility;
        manager.update = function () {
            const now = performance.now();
            if (PIXI.utils.isMobile.android.device && now < this.androidUpdateCount) {
                return;
            }
            this.androidUpdateCount = now + this.androidUpdateFrequency;
            if (!this.renderer.renderingToScreen) {
                return;
            }
            if (this.renderer.lastObjectRendered) {
                this.updateAccessibleObjects(this.renderer.lastObjectRendered);
            }
            const { x, y, width, height } = this.renderer.view.getBoundingClientRect();
            const { width: viewWidth, height: viewHeight, resolution } = this.renderer;
            const sx = width / viewWidth;
            const sy = height / viewHeight;
            let div = this.div;
            div.style.left = `${x}px`;
            div.style.top = `${y}px`;
            div.style.width = `${viewWidth}px`;
            div.style.height = `${viewHeight}px`;
            for (let i = 0; i < this.children.length; i++) {
                const child = this.children[i];
                if (child.renderId !== this.renderId) {
                    child._accessibleActive = false;
                    PIXI.utils.removeItems(this.children, i, 1);
                    this.div.removeChild(child._accessibleDiv);
                    this.pool.push(child._accessibleDiv);
                    child._accessibleDiv = null;
                    i--;
                }
                else {
                    div = child._accessibleDiv;
                    let hitArea = child.hitArea;
                    const wt = child.worldTransform;
                    if (child.hitArea) {
                        if (typeof PIXI.Circle !== "undefined" && hitArea instanceof PIXI.Circle) {
                            // Circle support
                            const centerX = (wt.tx + (hitArea.x * wt.a)) * sx;
                            const centerY = (wt.ty + (hitArea.y * wt.d)) * sy;
                            const radius = hitArea.radius * ((wt.a + wt.d) / 2) * ((sx + sy) / 2);
                            div.style.left = `${centerX - radius}px`;
                            div.style.top = `${centerY - radius}px`;
                            div.style.width = `${radius * 2}px`;
                            div.style.height = `${radius * 2}px`;
                            div.style.borderRadius = "50%";
                        }
                        else {
                            div.style.left = `${(wt.tx + (hitArea.x * wt.a)) * sx}px`;
                            div.style.top = `${(wt.ty + (hitArea.y * wt.d)) * sy}px`;
                            div.style.width = `${hitArea.width * wt.a * sx}px`;
                            div.style.height = `${hitArea.height * wt.d * sy}px`;
                            div.style.borderRadius = "";
                        }
                    }
                    else {
                        hitArea = child.getBounds();
                        if (typeof this.capHitArea === "function") {
                            this.capHitArea(hitArea);
                        }
                        div.style.left = `${hitArea.x * sx}px`;
                        div.style.top = `${hitArea.y * sy}px`;
                        div.style.width = `${hitArea.width * sx}px`;
                        div.style.height = `${hitArea.height * sy}px`;
                        div.style.borderRadius = "";
                    }
                    // update button titles and hints if they exist and they've changed
                    if (div.title !== child.accessibleTitle && child.accessibleTitle !== null) {
                        div.title = child.accessibleTitle;
                    }
                    if (div.getAttribute('aria-label') !== child.accessibleHint && child.accessibleHint !== null) {
                        div.setAttribute('aria-label', child.accessibleHint);
                    }
                    // the title or index may have changed, if so lets update it!
                    if (child.accessibleTitle !== div.title || child.tabIndex !== div.tabIndex) {
                        div.title = child.accessibleTitle;
                        div.tabIndex = child.tabIndex;
                        if (this.debug && typeof this.updateDebugHTML === "function")
                            this.updateDebugHTML(div);
                    }
                }
            }
            this.renderId++;
        };
    }
    addChild(child) {
        this.holder.addChild(child);
    }
    removeChild(child) {
        this.holder.removeChild(child);
    }
    setAutoResize(value) {
        this.properties.autoResize = value;
    }
    setBackgroundColour(value) {
        this.renderer.background.color = value;
    }
    getWindowSize() {
        let height = window.innerHeight;
        height -= this.verticalOffSet;
        return new PIXI.Point(window.innerWidth, height);
    }
    getStage() {
        return this.stage;
    }
    getRotated() {
        return this.rotated;
    }
    getOrientation() {
        return this.orientation;
    }
    getRenderer() {
        return this.renderer;
    }
    getView() {
        return this.view;
    }
    getHolder() {
        return this.holder;
    }
    getSafeTransform() {
        return this.safeTransform;
    }
    getDevice() {
        return this.properties.device;
    }
    getFrameRate() {
        return this.properties.targetFrameRate;
    }
    getResolution() {
        return this.properties.resolution;
    }
    getGameSize() {
        return this.properties.viewport.getGameSize();
    }
    getTicker() {
        return this.ticker;
    }
    getViewport() {
        return this.properties.viewport;
    }
    setVerticalOffSet(value) {
        this.verticalOffSet = value;
        this.view.style.top = `${value}px`;
    }
    isLandscape(forceOrientation) {
        if (forceOrientation) {
            return forceOrientation === OrientationType.LANDSCAPE;
        }
        return this.getViewport().isLandscape();
    }
    isPortrait(forceOrientation) {
        if (forceOrientation) {
            return forceOrientation === OrientationType.PORTRAIT;
        }
        return this.getViewport().isPortrait();
    }
    isResizing() {
        return this.resizeInProgress;
    }
    getMinimumScale() {
        return this.minimumScale;
    }
    setMinimumScale(value) {
        Logger.verbose("Set min scale: " + value);
        this.minimumScale = value;
    }
    getScreenReaderRegion() {
        return this.screenReaderRegion;
    }
    /**
     * Checks if the current mouse position is over the given displayObject.
     * @param displayObject The PIXI.DisplayObject to check.
     * @returns True if the mouse is over the displayObject, false otherwise.
     */
    isPointerOverDisplayObject(displayObject) {
        if (!displayObject || !displayObject.worldTransform)
            return false;
        // Convert mouse position to world coordinates
        const global = new PIXI.Point(this.mousePosition.x, this.mousePosition.y);
        // If the object has a containsPoint method, use it
        if (typeof displayObject.containsPoint === "function") {
            return displayObject.containsPoint(global);
        }
        // Otherwise, try to use getBounds
        if (typeof displayObject.getBounds === "function") {
            return displayObject.getBounds(true).contains(global.x, global.y);
        }
        return false;
    }
    static getInstance() {
        if (Renderer.instance === null) {
            Renderer.instance = new Renderer();
        }
        return Renderer.instance;
    }
}
Renderer.instance = null;
/*
 * ResizeEvent.ts
 */
export class ResizeEvent {
}
ResizeEvent.RESIZE = new EventObject("ResizeEvent.RESIZE");
ResizeEvent.PRE_ROTATE = new EventObject("ResizeEvent.PRE_ROTATE");
ResizeEvent.DEINITIALISE_TEXT = new EventObject("ResizeEvent.DEINITIALISE_TEXT");
ResizeEvent.ROTATE = new EventObject("ResizeEvent.ROTATE");
ResizeEvent.REINITIALISE_TEXT = new EventObject("ResizeEvent.REINITIALISE_TEXT");
ResizeEvent.POST_ROTATE = new EventObject("ResizeEvent.POST_ROTATE");
ResizeEvent.TRACK_TARGETS = new EventObject("ResizeEvent.TRACK_TARGETS");
ResizeEvent.TRACK_START = new EventObject("ResizeEvent.TRACK_START");
ResizeEvent.TRACK = new EventObject("ResizeEvent.TRACK");
ResizeEvent.TRACK_UPDATE_MANUAL = new EventObject("ResizeEvent.TRACK_UPDATE_MANUAL");
/**
 * TrackingProperties.ts
 */
export class TrackingProperties {
    constructor(id, offset = new PIXI.Point()) {
        this.offset = new PIXI.Point();
        this.id = id;
        this.offset = offset;
    }
    equals(other) {
        return other && this.id === other.id && this.offset.equals(other.offset);
    }
}
//# sourceMappingURL=Renderer.js.map